import React, { ReactNode } from 'react';
import { LucideIcon } from 'lucide-react';

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: LucideIcon;
  iconColor?: string;
  iconBgColor?: string;
  trend?: {
    value: string;
    isPositive: boolean;
  };
  onClick?: () => void;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  subtitle,
  icon: Icon,
  iconColor = 'text-blue-600',
  iconBgColor = 'bg-blue-100',
  trend,
  onClick
}) => {
  const cardClasses = onClick
    ? 'cursor-pointer hover:shadow-lg hover:-translate-y-1 transition-all duration-300'
    : 'transition-shadow duration-300';

  return (
    <div
      className={`bg-white rounded-2xl shadow-soft border border-gray-100 p-6 relative overflow-hidden ${cardClasses}`}
      onClick={onClick}
    >
      {/* Subtle Sand Drawing Pattern Background */}
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="cardPattern" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
              {/* Micro spiral */}
              <path d="M 50,50 Q 55,45 60,50 Q 65,55 60,60 Q 55,65 50,60 Q 45,55 50,50" 
                    stroke="currentColor" strokeWidth="0.8" fill="none" opacity="0.4"/>
              
              {/* Small dots */}
              <circle cx="20" cy="30" r="1.5" fill="currentColor" opacity="0.3"/>
              <circle cx="80" cy="70" r="1.5" fill="currentColor" opacity="0.3"/>
              <circle cx="30" cy="80" r="1" fill="currentColor" opacity="0.2"/>
              <circle cx="70" cy="20" r="1" fill="currentColor" opacity="0.2"/>
              
              {/* Tiny wave strokes */}
              <path d="M 10,50 Q 20,45 30,50" stroke="currentColor" strokeWidth="0.6" fill="none" opacity="0.3"/>
              <path d="M 70,80 Q 80,75 90,80" stroke="currentColor" strokeWidth="0.6" fill="none" opacity="0.3"/>
              
              {/* Triangle accent */}
              <path d="M 85,15 L 90,25 L 80,25 Z" stroke="currentColor" strokeWidth="0.5" fill="none" opacity="0.2"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#cardPattern)"/>
        </svg>
      </div>
      
      {/* Card Content */}
      <div className="relative z-10">
        <div className="flex items-start justify-between mb-4">
          <div className={`${iconBgColor} p-3 rounded-xl`}>
            <Icon className={`w-6 h-6 ${iconColor}`} />
          </div>
          {trend && (
            <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
              trend.isPositive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}>
              <span>{trend.isPositive ? '↑' : '↓'}</span>
              <span>{trend.value}</span>
            </div>
          )}
          </div>
        
        <div>
          <h3 className="text-3xl font-bold text-gray-900 mb-1">
            {value}
          </h3>
          <p className="text-sm font-medium text-gray-600">
            {title}
          </p>
          {subtitle && (
            <p className="text-xs text-gray-500 mt-1">
              {subtitle}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default StatCard;
